import type { BuiltInTool, BuiltInToolError } from './types'

/** The longest message a run writes with `os.chat_write`: a note to read in a thread, never a document. */
export const CHAT_WRITE_MAX_CHARS = 4000

/** How many messages `os.chat_read` hands a run when it names no limit, and the most either caller may ask for. */
export const CHAT_READ_DEFAULT_LIMIT = 50
export const CHAT_READ_MAX_LIMIT = 200

const THREADS =
    "In a run, the run's assigned member: the threads are always theirs, and an OS-owned task has none. Over the API and the MCP server, any thread of the enterprise the token can reach."

const NO_MEMBER: BuiltInToolError = {
    code: 'no_assigned_member',
    when: 'A run with no assigned member called it, so there is no thread.',
    handling: 'tool_error',
}

const NOT_A_THREAD: BuiltInToolError = {
    code: 'chat_not_found',
    when: "No thread has that id: in a run, including another member's thread, another person's, and a conversation opened for one job.",
    handling: 'tool_error',
}

/**
 * Chat threads, as `os.mail` is the mailbox: `os.chat_list` and `os.chat_read` over the conversations a member has
 * with the enterprise's people, `os.chat_write` to say something in one, and `os.chat_open` to start one.
 *
 * Every caller reads and writes as who it is. A run is its assigned member: it sees that member's threads only, and
 * what it writes is the member speaking, filed for the people to read, with no reply turn. An API token or the MCP
 * server is a person's side of the same thread: it reads any thread of the enterprise, and what it writes is a
 * message to the member, who replies. Each entry's `api` is the endpoint that side calls, so the MCP server serves
 * these as they stand (#1011): the schemas are strict, and one schema covers both sides.
 */
export const OS_CHAT_THREAD_TOOLS: readonly BuiltInTool[] = [
    {
        name: 'os.chat_list',
        family: 'os.chat_threads',
        summary: 'List chat threads, most recently active first: who each is with, its state and its last message.',
        description:
            'List chat threads, most recently active first: each thread’s id, its state (idle, replying or closed), ' +
            'and its last message. In a run these are the enterprise owner’s threads with the member doing the ' +
            'run, the ones the OS files into: never another person’s private chats, and memberId is ignored. Over ' +
            'the API they are the threads you own, or only one member’s when you pass memberId. No message bodies ' +
            'beyond the last: read a thread with os.chat_read. A conversation opened for one job, such as ' +
            'designing a task, is never listed.',
        inputSchema: {
            type: 'object',
            properties: {
                memberId: {
                    type: 'string',
                    description: 'Over the API: only threads with this member. A run always lists its own member’s.',
                },
            },
            additionalProperties: false,
        },
        returns:
            'To a run: `{ threads }`, each with `id`, `status` (`idle`, `replying` or `closed`), `lastMessage` (`role`, `content`, `createdAt`, or null in an empty thread), `lastReadAt` (when a person last opened it, or null), `createdAt` and `updatedAt`. Over the API: the Chat page’s rows as they are, which add the `member` and the `unread` and `unreadCount` of the person’s side.',
        callers: ['run', 'api', 'mcp'],
        effect: 'read',
        retry: 'safe',
        needs: { member: THREADS },
        errors: [NO_MEMBER],
        api: { method: 'GET', path: '/v1/chats', scope: 'chats:read' },
        examples: [{ input: {} }],
        public: true,
    },
    {
        name: 'os.chat_read',
        family: 'os.chat_threads',
        summary: 'Read one chat thread: its messages, oldest first.',
        description:
            'Read one chat thread: its messages, oldest first, each with who wrote it. Pass id from os.chat_list, ' +
            `and optionally limit to read only the newest messages (up to ${CHAT_READ_MAX_LIMIT}). In a run you ` +
            `get what was said (people’s messages and the member’s), the newest ${CHAT_READ_DEFAULT_LIMIT} unless ` +
            'you pass limit, in one of the threads os.chat_list shows you, and reading does not mark the thread ' +
            'read. Over the API you get every row, tool calls included, in a thread you own or reach by its link, ' +
            'and reading marks it read for its owner, as opening it in the portal does.',
        inputSchema: {
            type: 'object',
            properties: {
                id: { type: 'string', description: 'A thread id from os.chat_list' },
                limit: {
                    type: 'integer',
                    minimum: 1,
                    maximum: CHAT_READ_MAX_LIMIT,
                    description: `Only the newest messages, up to ${CHAT_READ_MAX_LIMIT}. In a run it defaults to ${CHAT_READ_DEFAULT_LIMIT}; over the API, to all of them.`,
                },
            },
            required: ['id'],
            additionalProperties: false,
        },
        returns:
            'To a run: `{ id, status, total, messages }`, where `total` counts every message said in the thread and each message has `id`, `role` (`user` for a person or an API token, `assistant` for the member), `content`, `sender` (the name of whoever wrote a `user` message, else null) and `createdAt`. Over the API: the thread as the portal reads it, with its `member` and every row, each with its `sender`.',
        callers: ['run', 'api', 'mcp'],
        effect: 'read',
        sideEffects:
            'Over the API and the MCP server, marks the thread read, the same as opening it in the portal. In a run, nothing.',
        retry: 'safe',
        needs: { member: THREADS },
        errors: [NO_MEMBER, NOT_A_THREAD],
        api: { method: 'GET', path: '/v1/chats/:id', scope: 'chats:read' },
        examples: [{ input: { id: '5f0c1d2e-0000-4000-8000-000000000021', limit: 20 } }],
        public: true,
    },
    {
        name: 'os.chat_write',
        family: 'os.chat_threads',
        summary: 'Write one message in a chat thread, as yourself.',
        description:
            'Write one message in a chat thread, as yourself. Pass content: Markdown. In a run you write as the ' +
            'member doing the run, for the enterprise’s people to read, and it is drawn as the member’s formatted ' +
            'text: paragraphs with their line breaks kept, bulleted and numbered lists, bold, and links, written ' +
            '[text](https://…) or as a bare web address. It shows as unread until someone opens the thread, nobody ' +
            'replies to the run, and the run carries on. Leave id out to write in the enterprise owner’s open thread ' +
            'with the member (one the owner owns is opened if there is none), or pass id from os.chat_list. At most ' +
            `${CHAT_WRITE_MAX_CHARS} characters in a run. For a question you ` +
            'need answered, use os.ask_ceo. Over the API, id is required, you write as the person or token calling, ' +
            'shown as you typed it with web addresses linked, and the member replies: the response is the thread ' +
            'with that reply in it. Sends no mail and opens no gate. A repeat writes the message again.',
        inputSchema: {
            type: 'object',
            properties: {
                id: {
                    type: 'string',
                    description:
                        'The thread, from os.chat_list. A run may leave it out to write in the enterprise owner’s open thread with its member. Required over the API.',
                },
                content: { type: 'string', description: 'The message, as Markdown' },
            },
            required: ['content'],
            additionalProperties: false,
        },
        returns:
            'To a run: `{ written: true, id, messageId }`, the thread it went in and the message. Over the API: the thread as os.chat_read returns it, with the member’s reply.',
        callers: ['run', 'api', 'mcp'],
        effect: 'write',
        sideEffects:
            'In a run, one message from the member in the thread. Over the API, one message from the caller and a reply turn by the member, which spends on the enterprise’s model keys.',
        retry: 'unsafe',
        retryNote: 'A repeat writes the message again: call it once for each thing you have to say.',
        needs: { member: THREADS },
        errors: [
            NO_MEMBER,
            NOT_A_THREAD,
            { code: 'empty_message', when: '`content` is missing, not text, or blank.', handling: 'tool_error' },
            {
                code: 'message_too_long',
                when: `In a run, \`content\` is longer than ${CHAT_WRITE_MAX_CHARS} characters.`,
                handling: 'tool_error',
            },
            { code: 'chat_closed', when: 'The thread is closed.', handling: 'tool_error' },
            {
                code: 'chat_replying',
                when: 'Over the API, the member is still replying to an earlier message in the thread.',
                handling: 'tool_error',
            },
        ],
        api: { method: 'POST', path: '/v1/chats/:id/messages', scope: 'chats:write' },
        examples: [
            { input: { content: 'New enquiry\nFrom: jo@example.com\nAsking about: the Team plan' } },
            {
                input: { id: '5f0c1d2e-0000-4000-8000-000000000021', content: 'Where did last week’s report go?' },
                note: 'Over the API: a person’s message to the member, answered in the response.',
            },
        ],
        public: true,
    },
    {
        name: 'os.chat_open',
        family: 'os.chat_threads',
        summary: 'Open a new chat thread with a member.',
        description:
            'Open a new, empty chat thread with a member, and get its id to write in with os.chat_write. Pass ' +
            'memberId. Nothing is said and nothing is spent until a message is written. A run cannot call it: a ' +
            'run’s os.chat_write opens its member’s thread when there is none.',
        inputSchema: {
            type: 'object',
            properties: { memberId: { type: 'string', description: 'The member to talk to' } },
            required: ['memberId'],
            additionalProperties: false,
        },
        returns: 'The new thread: its `id`, `memberId`, `status` (`idle`) and times.',
        callers: ['api', 'mcp'],
        effect: 'write',
        retry: 'unsafe',
        retryNote: 'A repeat opens another thread with the same member.',
        errors: [
            { code: 'memberId_required', when: '`memberId` is missing.', handling: 'tool_error' },
            { code: 'member_not_found', when: 'No member of this enterprise has that id.', handling: 'tool_error' },
        ],
        api: { method: 'POST', path: '/v1/chats', scope: 'chats:write' },
        examples: [{ input: { memberId: '5f0c1d2e-0000-4000-8000-000000000007' } }],
        public: true,
    },
]
